import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { UserPlus, UserCheck } from 'lucide-react';
import clsx from 'clsx';
import { followApi } from '@/api/follow';

/**
 * Подписаться/отписаться. Показывается только для чужого профиля.
 * Оптимистичный update делает переключение мгновенным.
 */
export default function FollowButton({ userId, className }: { userId: string; className?: string }) {
  const qc = useQueryClient();
  const { data: following } = useQuery({
    queryKey: ['follow', userId, 'status'],
    queryFn: () => followApi.status(userId),
  });

  const mut = useMutation({
    mutationFn: async () => {
      if (following) await followApi.unfollow(userId);
      else await followApi.follow(userId);
    },
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: ['follow', userId, 'status'] });
      const prev = qc.getQueryData<boolean>(['follow', userId, 'status']);
      qc.setQueryData(['follow', userId, 'status'], !prev);
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx) qc.setQueryData(['follow', userId, 'status'], ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['follow', userId, 'status'] });
    },
  });

  return (
    <button
      onClick={() => mut.mutate()}
      disabled={mut.isPending}
      className={clsx(
        'inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium transition-colors disabled:opacity-50',
        following
          ? 'bg-surface-800 text-surface-200 hover:bg-surface-700'
          : 'bg-brand-600 text-white hover:bg-brand-500',
        className,
      )}
    >
      {following ? <UserCheck size={14} /> : <UserPlus size={14} />}
      {following ? 'Подписан' : 'Подписаться'}
    </button>
  );
}
